import React from 'react';

export type SwitchTone = 'secondary' | 'primary';

export interface SwitchProps {
  /** Binary checked state */
  checked: boolean;
  /** Callback invoked when checked state changes */
  onCheckedChange?: (checked: boolean) => void;
  /** Active track color tone: 'secondary' (default green) or 'primary' (blue) */
  tone?: SwitchTone;
  /** Disabled interaction state */
  disabled?: boolean;
  /** Optional accessible label displayed next to switch */
  label?: string;
  /** Place label after the switch in DOM order ('end') or before it (default 'start') */
  labelPosition?: 'start' | 'end';
  /** Extra classes for the label and container gap override */
  labelClassName?: string;
  /** Optional HTML id linking label and switch */
  id?: string;
  /** Additional container class */
  className?: string;
  /** Explicit aria-label when no visible label is present */
  'aria-label'?: string;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      onCheckedChange,
      tone = 'secondary',
      disabled = false,
      label,
      labelPosition = 'start',
      labelClassName,
      id,
      className = '',
      'aria-label': ariaLabelProp
    },
    ref
  ) => {
    const toneCheckedClasses: Record<SwitchTone, string> = {
      secondary: 'bg-secondary',
      primary: 'bg-primary'
    };

    const accessibleLabel = label ?? ariaLabelProp;

    return (
      <div className={`inline-flex items-center gap-3 select-none ${labelPosition === 'end' ? 'flex-row-reverse' : ''} ${className}`}>
        {label && (
          <label
            htmlFor={id}
            className={`font-medium ${labelClassName ?? 'text-sm'} ${
              disabled ? 'opacity-50 cursor-not-allowed' : `cursor-pointer ${labelClassName ? '' : 'text-base-content'}`
            }`}
          >
            {label}
          </label>
        )}
        <button
          ref={ref}
          id={id}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={accessibleLabel}
          disabled={disabled}
          onClick={() => onCheckedChange?.(!checked)}
          className={`relative inline-flex h-[26px] w-11 shrink-0 cursor-pointer items-center rounded-full p-[3px] border border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 ring-primary ring-offset-2 ring-offset-base-100 disabled:opacity-50 disabled:cursor-not-allowed focus-ring ${
            checked ? toneCheckedClasses[tone] : 'bg-base-300'
          }`}
        >
          <span
            className={`pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ease-in-out ${
              checked ? 'rtl:-translate-x-[18px] ltr:translate-x-[18px]' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    );
  }
);

Switch.displayName = 'Switch';
